import { useState, useEffect, useCallback, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, RefreshCw, Package, Search, Trash2, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { StockPanel } from "@/components/StockPanel";
import { DiscountPanel } from "@/components/DiscountPanel";
import { getAdminOrders, updateAdminOrder, deleteAdminOrder, getToken, clearToken } from "@/lib/api";

const gbp = (n) => `£${Number(n || 0).toFixed(2)}`;
const fmtDate = (s) => { try { return new Date(s).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return s; } };

const STATUS_STYLES = {
  Open: "bg-amber-100 text-amber-700",
  Completed: "bg-emerald-100 text-emerald-700",
  Cancelled: "bg-rose-100 text-rose-700",
};
const PAY_STYLES = {
  Unpaid: "bg-slate-100 text-slate-600",
  Paid: "bg-emerald-100 text-emerald-700",
};

export default function AdminOrders() {
  const navigate = useNavigate();
  const [data, setData] = useState({ orders: [], order_statuses: [], payment_statuses: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAdminOrders();
      setData(res);
    } catch (err) {
      if (err?.response?.status === 401) { clearToken(); navigate("/control", { replace: true }); return; }
      toast.error("Could not load orders.");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!getToken()) { navigate("/control", { replace: true }); return; }
    load();
  }, [load, navigate]);

  const logout = () => { clearToken(); navigate("/control", { replace: true }); };

  const patch = async (id, updates) => {
    try {
      const updated = await updateAdminOrder(id, updates);
      setData((d) => ({ ...d, orders: d.orders.map((o) => (o.id === id ? { ...o, ...updated } : o)) }));
      toast.success("Order updated");
    } catch {
      toast.error("Update failed");
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    const id = toDelete.id;
    try {
      await deleteAdminOrder(id);
      setData((d) => ({ ...d, orders: d.orders.filter((o) => o.id !== id) }));
      toast.success("Order deleted");
    } catch {
      toast.error("Delete failed");
    } finally {
      setToDelete(null);
    }
  };

  const term = q.trim().toLowerCase();
  const orders = term
    ? data.orders.filter((o) =>
        [o.order_number, o.full_name, o.email, o.city, o.postcode].some((v) => (v || "").toLowerCase().includes(term)))
    : data.orders;

  const revenue = data.orders.filter((o) => o.payment_status === "Paid").reduce((s, o) => s + Number(o.total || 0), 0);
  const openCount = data.orders.filter((o) => (o.status || "Open") === "Open").length;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2">
            <span className="grid h-9 w-9 place-items-center rounded-lg bg-blue-600 text-white"><Package className="h-5 w-5" /></span>
            <div>
              <p className="font-display text-lg font-bold leading-none text-slate-900">Orders</p>
              <p className="text-xs text-slate-500">PepsWorld UK admin</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate("/control/analytics")} data-testid="nav-to-analytics" className="border-slate-200 bg-transparent text-slate-700 hover:bg-slate-100"><BarChart3 className="mr-1.5 h-4 w-4" /> <span className="hidden sm:inline">Analytics</span></Button>
            <Button variant="outline" size="sm" onClick={load} data-testid="admin-refresh" className="border-slate-200 bg-transparent text-slate-700 hover:bg-slate-100"><RefreshCw className="mr-1.5 h-4 w-4" /> <span className="hidden sm:inline">Refresh</span></Button>
            <Button variant="outline" size="sm" onClick={logout} data-testid="admin-logout" className="border-slate-200 bg-transparent text-slate-700 hover:bg-slate-100"><LogOut className="mr-1.5 h-4 w-4" /> <span className="hidden sm:inline">Sign out</span></Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* stats */}
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="rounded-xl bg-white p-5 ring-1 ring-slate-100"><p className="text-xs uppercase tracking-wide text-slate-500">Total orders</p><p data-testid="stat-total" className="mt-1 font-display text-2xl font-bold text-slate-900">{data.orders.length}</p></div>
          <div className="rounded-xl bg-white p-5 ring-1 ring-slate-100"><p className="text-xs uppercase tracking-wide text-slate-500">Open</p><p className="mt-1 font-display text-2xl font-bold text-amber-600">{openCount}</p></div>
          <div className="rounded-xl bg-white p-5 ring-1 ring-slate-100"><p className="text-xs uppercase tracking-wide text-slate-500">Paid revenue</p><p className="mt-1 font-display text-2xl font-bold text-emerald-600">{gbp(revenue)}</p></div>
        </div>

        <StockPanel />
        <DiscountPanel />

        <div className="mt-6 flex items-center gap-2">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search order no, name, email, postcode..." className="bg-white border-slate-200 pl-9" />
          </div>
          <span className="text-sm text-slate-500">{orders.length} shown</span>
        </div>

        {/* table */}
        <div className="mt-4 overflow-hidden rounded-xl bg-white ring-1 ring-slate-100">
          {loading ? (
            <p className="p-10 text-center text-slate-500">Loading orders...</p>
          ) : orders.length === 0 ? (
            <p data-testid="admin-no-orders" className="p-10 text-center text-slate-500">No orders found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[820px] text-sm" data-testid="admin-orders-table">
                <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Order</th>
                    <th className="px-4 py-3">Customer</th>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3 text-right">Total</th>
                    <th className="px-4 py-3">Payment</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {orders.map((o) => (
                    <Fragment key={o.id}>
                      <tr data-testid={`order-row-${o.order_number}`} className="cursor-pointer hover:bg-slate-50" onClick={() => setExpanded(expanded === o.id ? null : o.id)}>
                        <td className="px-4 py-3 font-mono2 text-xs font-medium text-slate-900">{o.order_number}</td>
                        <td className="px-4 py-3"><p className="font-medium text-slate-900">{o.full_name}</p><p className="text-xs text-slate-500">{o.email}</p></td>
                        <td className="px-4 py-3 text-slate-600">{fmtDate(o.created_at)}</td>
                        <td className="px-4 py-3 text-right font-semibold text-slate-900">{gbp(o.total)}</td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <Select value={o.payment_status || "Unpaid"} onValueChange={(v) => patch(o.id, { payment_status: v })}>
                            <SelectTrigger data-testid={`pay-select-${o.order_number}`} className={`h-8 w-[120px] border-0 ${PAY_STYLES[o.payment_status || "Unpaid"]}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{data.payment_statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <Select value={o.status || "Open"} onValueChange={(v) => patch(o.id, { status: v })}>
                            <SelectTrigger data-testid={`status-select-${o.order_number}`} className={`h-8 w-[130px] border-0 ${STATUS_STYLES[o.status || "Open"]}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{data.order_statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                        <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" data-testid={`delete-order-${o.order_number}`} onClick={() => setToDelete(o)} className="h-8 w-8 text-slate-400 hover:bg-rose-50 hover:text-rose-600">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                      {expanded === o.id && (
                        <tr className="bg-slate-50/70">
                          <td colSpan={7} className="px-4 py-4">
                            <div className="grid gap-4 sm:grid-cols-2">
                              <div>
                                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Delivery</p>
                                <p className="mt-1 text-sm text-slate-700">{o.full_name}{o.phone ? ` · ${o.phone}` : ""}</p>
                                <p className="text-sm text-slate-700">{o.address}, {o.city}, {o.postcode}, {o.country}</p>
                                {o.notes ? <p className="mt-1 text-sm text-slate-500">Notes: {o.notes}</p> : null}
                              </div>
                              <div>
                                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Items</p>
                                <ul className="mt-1 space-y-1">
                                  {(o.items || []).map((it, i) => (
                                    <li key={i} className="flex justify-between text-sm text-slate-700"><span>{it.name} × {it.qty}</span><span>{gbp((it.price || 0) * it.qty)}</span></li>
                                  ))}
                                </ul>
                                <div className="mt-2 flex justify-between border-t border-slate-200 pt-2 text-sm"><span className="text-slate-500">Shipping</span><span>{o.shipping === 0 ? "Free" : gbp(o.shipping)}</span></div>
                                <div className="flex justify-between text-sm font-semibold"><span>Total</span><span>{gbp(o.total)}</span></div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      <AlertDialog open={!!toDelete} onOpenChange={(open) => !open && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to delete this order?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete ? `Order ${toDelete.order_number} (${toDelete.full_name}) will be permanently removed. This cannot be undone.` : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-confirm" onClick={confirmDelete} className="bg-rose-600 text-white hover:bg-rose-700">Delete order</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
